import EmptyState from '@/components/EmptyState';
import { AiDock } from '@/features/ai-changeset/components/AiDock';
import { runPositions } from '@/features/ai-changeset/utils/changeset-view';
import { dateRange, ExceptionRow } from '@/features/opening-hours/components/ExceptionRow';
import { useDeleteException, useExceptions } from '@/features/opening-hours/hooks/useExceptions';
import { useExceptionsQuickEdit } from '@/features/opening-hours/hooks/useExceptionsQuickEdit';
import { useT } from '@/i18n';
import type { Exception } from '@restaurant/api-contracts';
import {
  Block,
  BlockFooter,
  List,
  Navbar,
  NavRight,
  Link as F7Link,
  Page,
  Preloader,
  f7,
} from 'framework7-react';
import { useSettingsDetailBackLink } from '@/utils/useMediaQuery';

function ExceptionsPage({ f7router }: { f7router: { navigate: (url: string) => void } }) {
  const t = useT();
  const settingsBackLink = useSettingsDetailBackLink();

  const { data, isLoading, error } = useExceptions();
  const deleteException = useDeleteException();
  const quickEdit = useExceptionsQuickEdit(data);
  const { draft } = quickEdit;
  const runs = runPositions(draft.rows.map((row) => row.status !== null));

  // Push the form on top of this list (slide-in + Back) WITHIN this page's own
  // view - the nested settings viewport on desktop, the settings tab on mobile.
  // Using the injected router (not f7.views.current) keeps the push in the right
  // view regardless of which one is active.
  const navTo = (path: string) => f7router.navigate(path);
  const openAdd = () => navTo('/settings/holidays/new');
  const openEdit = (ex: Exception) => navTo(`/settings/holidays/${ex.id}`);

  const confirmDelete = (ex: Exception) => {
    const label = ex.reason || dateRange(ex);
    f7.dialog.confirm(
      t.openingHours.removeClosureQuestion(label),
      t.openingHours.removeClosureConfirm,
      () => {
        deleteException.mutate(ex.id, {
          onSuccess: () =>
            f7.toast.show({
              text: t.openingHours.closureRemoved,
              closeTimeout: 2000,
              position: 'bottom',
            }),
          onError: (e) =>
            f7.toast.show({
              text: e instanceof Error ? e.message : t.openingHours.couldNotRemove,
              closeTimeout: 2500,
              position: 'bottom',
            }),
        });
      },
    );
  };

  return (
    <Page name="opening-hours-exceptions">
      <Navbar title={t.openingHours.holidaysClosures} backLink={settingsBackLink}>
        <NavRight>
          <F7Link iconF7="plus" onClick={openAdd} />
        </NavRight>
      </Navbar>

      {quickEdit.canUseAi && data && (
        <AiDock
          dock={quickEdit}
          testId="exceptions-ai-dock"
          title={t.aiDock.exceptionsTitle}
          placeholder={t.exceptionsQuickEdit.placeholder}
        />
      )}

      {isLoading && (
        <Block className="text-align-center">
          <Preloader />
        </Block>
      )}
      {error && (
        <EmptyState
          icon="exclamationmark_triangle"
          iconColor="#ff9500"
          title={t.openingHours.couldNotLoadClosures}
          description={(error as Error).message}
        />
      )}

      {data && draft.rows.length === 0 && (
        <EmptyState
          icon="calendar_badge_minus"
          title={t.openingHours.noClosuresYet}
          description={t.openingHours.noClosuresDescription}
        />
      )}

      {draft.rows.length > 0 && (
        <>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd mediaList>
            {draft.rows.map((row, index) => {
              const exception = data?.find((item) => item.id === row.id);
              const locked = row.status !== null;
              return ExceptionRow({
                key: row.id,
                row,
                run: runs[index] ?? null,
                store: quickEdit.navigation.store,
                t,
                onOpen: !locked && exception ? () => openEdit(exception) : null,
                onRemove: !locked && exception ? () => confirmDelete(exception) : null,
              });
            })}
          </List>
          <BlockFooter>{t.openingHours.closuresOverrideNote}</BlockFooter>
        </>
      )}
    </Page>
  );
}

export default ExceptionsPage;
